/* eslint-disable max-len */
import { useApolloClient } from '@apollo/client';
import { Button, Card, Col, Form, message, Row, Space } from 'antd';
import { Formik } from 'formik';
import { omit } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type PayGatePaymentModuleSpecsFragment } from '../../../../api/fragments/PayGatePaymentModuleSpecs';
import { type PayGatePaymentSettingsSpecFragment } from '../../../../api/fragments/PayGatePaymentSettingsSpec';
import {
    type UpdatePayGatePaymentSettingsMutation,
    type UpdatePayGatePaymentSettingsMutationVariables,
    UpdatePayGatePaymentSettingsDocument,
} from '../../../../api/mutations/updatePayGatePaymentSettings';
import { type PayGatePaymentUpdateSettingsInput } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import InputNumberField from '../../../../components/fields/InputNumberField';
import PasswordField from '../../../../components/fields/PasswordField';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validator from './validators';

export type FormValues = PayGatePaymentUpdateSettingsInput;

export type UpdatePayGatePaymentSettingInnerPageProps = {
    module: PayGatePaymentModuleSpecsFragment;
    paymentSettings: PayGatePaymentSettingsSpecFragment & {
        encryptionKey?: string;
    };
};

const colSpan = { lg: 8, md: 12, xs: 24 };

const UpdatePayGatePaymentSettingInnerPage = ({
    module,
    paymentSettings,
}: UpdatePayGatePaymentSettingInnerPageProps) => {
    const moduleId = module.id;
    const paymentSettingsId = paymentSettings.id;

    const { t } = useTranslation(['payGatePaymentModuleDetails']);
    const apolloClient = useApolloClient();
    const navigate = useNavigate();

    const initialValues = useMemo(
        (): FormValues => ({
            ...omit(['id', 'paymentModuleId'], paymentSettings),
        }),
        [paymentSettings]
    );

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('paygatePaymentModuleDetails:paymentSettingsDrawer.messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<UpdatePayGatePaymentSettingsMutation, UpdatePayGatePaymentSettingsMutationVariables>({
                    mutation: UpdatePayGatePaymentSettingsDocument,
                    variables: { moduleId, id: paymentSettingsId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('paygatePaymentModuleDetails:paymentSettingsDrawer.messages.updateSuccess'),
            });

            navigate(`/admin/system/modules/${moduleId}`);
        },
        [t, apolloClient, moduleId, paymentSettingsId, navigate]
    );

    const validate = useValidator(validator(false));

    return (
        <ConsolePageWithHeader
            footer={[
                <Button key="submit" form="paymentSettingsForm" htmlType="submit" type="primary">
                    {t('paygatePaymentModuleDetails:paymentSettingsDrawer.updateButton')}
                </Button>,
            ]}
            onBack={() => navigate(`/admin/system/modules/${moduleId}`)}
            title={t('paygatePaymentModuleDetails:paymentSettingsDrawer.title.update')}
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form
                        id="paymentSettingsForm"
                        layout="vertical"
                        name="paymentSettingsForm"
                        onSubmitCapture={handleSubmit}
                    >
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Card title={t('paygatePaymentModuleDetails:dividers.mainDetails')}>
                                <Row gutter={14}>
                                    <Col {...colSpan}>
                                        <InputField
                                            {...t(
                                                'paygatePaymentModuleDetails:paymentSettingsDrawer.fields.displayName',
                                                {
                                                    returnObjects: true,
                                                }
                                            )}
                                            name="displayName"
                                            required
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <InputField
                                            {...t('paygatePaymentModuleDetails:paymentSettingsDrawer.fields.currency', {
                                                returnObjects: true,
                                            })}
                                            name="currency"
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <InputNumberField
                                            {...t('paygatePaymentModuleDetails:paymentSettingsDrawer.fields.amount', {
                                                returnObjects: true,
                                            })}
                                            name="amount"
                                        />
                                    </Col>
                                </Row>
                            </Card>
                            <Card title={t('paygatePaymentModuleDetails:dividers.paymentSettings')}>
                                <InputField
                                    {...t('paygatePaymentModuleDetails:paymentSettingsDrawer.fields.apikey', {
                                        returnObjects: true,
                                    })}
                                    name="apiKey"
                                />
                                <PasswordField
                                    {...t('paygatePaymentModuleDetails:paymentSettingsDrawer.fields.encryptionKey', {
                                        returnObjects: true,
                                    })}
                                    name="encryptionKey"
                                />
                                <InputField
                                    {...t('paygatePaymentModuleDetails:paymentSettingsDrawer.fields.paymentEndpoint', {
                                        returnObjects: true,
                                    })}
                                    name="paymentEndpoint"
                                />
                            </Card>
                        </Space>
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default UpdatePayGatePaymentSettingInnerPage;
